import { useQuery, useMutation } from "@tanstack/react-query";
import { chatApi } from "../api";
import { useChatbotStore, useChatStateStore, useMessagesStore, useProductsStore, useUserStore, useUtmStore } from "../../zustand/store";
import { groupProductsUsingReduce, logToConsole } from "../../utils/utils";
import { useRef, useEffect } from "react";
import { contents } from "../../data/contents";

export const useChatbot = (partnerId) => {
    const { chatUserId } = useUserStore();
    const { setChatbot } = useChatbotStore();
    return useQuery({
        queryKey: ['chatbot', partnerId],
        queryFn: async () => {
            const response = await chatApi.get(`${process.env.REACT_APP_CHATBOT_ENDPOINT}/${partnerId}?chatUserId=${chatUserId}`);
            setChatbot(response.data);
            return response.data;
        },
        enabled: !!partnerId && !!chatUserId,
    });
};

export const useMessages = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { products, setProducts, clearProducts, setStructuredProducts } = useProductsStore();
    const { setMessages } = useMessagesStore();
    const { currentItemId, setIsChatbotPending, setIsNewProduct, setNewProductCount, language } = useChatStateStore();
    const { setIsFormSubmitted } = useChatStateStore();
    const { refetch: refetchExample } = useExampleQuery();

    return useMutation({
        mutationFn: async (variables) => {
            try {
                setIsFormSubmitted('initial');
                const { message, itemId, preAnswers } = variables;
                const response = await chatApi.post(`${process.env.REACT_APP_CHAT_ENDPOINT}/${partnerId}`, {
                    requestMessage: String(message),
                    itemIds: itemId,
                    chatUserId: String(chatUserId),
                    channelId: isMobileDevice ? 'mobile' : 'web',
                    preAnswers: preAnswers,
                });

                if (response.data.text) {
                    logToConsole(response.data);
                    if (response.data.product.length > 0) {
                        const newProducts = [...response.data.product, ...products];
                        const newStructuredProducts = groupProductsUsingReduce(response.data.product, 3);
                        clearProducts();
                        setProducts(newProducts);
                        setStructuredProducts(newStructuredProducts);
                        setIsNewProduct(true);
                        setNewProductCount(response.data.product.length);
                    }
                    setIsChatbotPending(false);
                    if (currentItemId.length > 0) {
                        refetchExample();
                    }
                    return response.data;
                }
            } catch (error) {
                logToConsole({ textResp: error.message });
                setIsChatbotPending(false);
                console.log('error', error.message);
                setMessages([{ message: contents.message[language].networkError, isChatbot: true }])
            }
        },
    });
};

// custom hook for sending message
export const useSendMessage = () => {
    const { mutate: sendMessages, isPending, isSuccess } = useMessages();
    const { currentItemId } = useChatStateStore();
    const { setMessages } = useMessagesStore();
    const { clearExamples, clearFollowupQuestion } = useProductsStore();

    return {
        sendMessage: (messageInput, isChatbot, preAnswers = []) => {
            clearExamples();
            clearFollowupQuestion();
            sendMessages({
                message: messageInput,
                itemId: currentItemId,
                preAnswers: preAnswers,
            });
            setMessages([{
                message: messageInput,
                isChatbot: isChatbot,
            }]);
        },
        isPending,
        isSuccess,
    };
}

export const useSendMessageWithWebSocket = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { setMessages, streamMessage, clearStreamMessage, setStreamMessage, guidelineContent, guidelineTitle, guidelineId, isBuilder, setIsBuilder } = useMessagesStore();
    const { 
        setIsStreaming, 
        connectionId, 
        setConnectionId, 
        currentItemId, 
        setIsChatbotPending, 
        setIsPDPChat, 
        setIsNewProduct, 
        language, 
        isConsole,
        setIsFormSubmitted,
        setIsLastIntentProductRecommendation
    } = useChatStateStore();
    const { setExamples, clearFollowupQuestion } = useProductsStore();
    const { mutate: refetchProductInfo } = useFetchProductInfo();
    const { refetch: refetchExample } = useExampleQuery();
    const isBuilderRef = useRef(isBuilder);

    useEffect(() => {
        isBuilderRef.current = isBuilder;
    }, [isBuilder]);

    return useMutation({
        mutationFn: async (variables) => {
            const { messageInput, preQuestionAnswers = [], preAnswers = [] } = variables;

            setIsFormSubmitted('initial');

            setMessages([{
                message: messageInput,
                isChatbot: false,
            }]);

            let messageOutput = '';
            let localConnectionId = null;
            const ws = new WebSocket(process.env.REACT_APP_WSS_CHAT);

            setIsNewProduct(false);
            setIsPDPChat(false);

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    "action": "chat",
                    "chatUserId": chatUserId,
                    "channelId": isMobileDevice ? 'mobile' : 'web',
                    "requestMessage": messageInput,
                    "itemIds": currentItemId,
                    "preQuestionAnswers": preQuestionAnswers || [],
                    "preAnswers": preAnswers || [],
                    "externalKey": partnerId,
                    "connectionId": connectionId,
                    "guidelineId": guidelineId,
                    "guidelineTitle": guidelineTitle,
                    "guidelineContent": guidelineContent,
                    "isConsole": isConsole,
                }));
            };

            const productFetchingIntentList = ['제공', '추천', '딥리서치'];
            let intent = null;

            ws.onmessage = (event) => {
                const data = event.data ? JSON.parse(event.data) : null;
                if (data && data.connectionId) {
                    setConnectionId(data.connectionId);
                    localConnectionId = data.connectionId;
                    clearFollowupQuestion();
                } else if (data && data.isBuilder !== undefined) {
                    setIsBuilder(data.isBuilder);
                    isBuilderRef.current = data.isBuilder;
                    window.parent.postMessage({
                        type: 'isBuilderTransition',
                        isBuilder: data.isBuilder,
                    }, '*');
                } else if (data && data.type === 'chatStream') {
                    setIsChatbotPending(false);
                    setStreamMessage(data.token);
                    messageOutput += data.token;
                    intent = data.intent;
                } else if (data && data.guidelineIntent) {
                    window.parent.postMessage({
                        type: 'guidelineIntent',
                        guidelineIntent: data.guidelineIntent,
                    }, '*');
                } else if (data && data.type === 'guidelineContent') {
                    window.parent.postMessage({
                        type: 'guidelineContent',
                        content: data.token,
                    }, '*');
                } else if (data && data.type === 'guidelineTitle') {
                    window.parent.postMessage({
                        type: 'guidelineTitle',
                        title: data.token,
                    }, '*');
                } else if (data && data.done) {
                    window.parent.postMessage({
                        type: 'streamEnd',
                        streamEnd: true,
                        streamType: variables?.streamType || 'chat',
                    }, '*');
                    if (intent && productFetchingIntentList.includes(intent)) {
                        setIsLastIntentProductRecommendation(true);
                        refetchProductInfo(localConnectionId);
                    } else {
                        setIsLastIntentProductRecommendation(false);
                        refetchExample();
                    }
                    ws.close();
                    setMessages([{
                        message: messageOutput,
                        isChatbot: true,
                        type: 'stream',
                        isBuilder: isBuilderRef.current
                    }]);
                    logToConsole({ text: messageOutput });
                    setIsStreaming(false);
                    clearStreamMessage();
                    setIsChatbotPending(false);
                } else if (data && data.error) {
                    console.log('data.error', data.error);
                    setMessages([{ message: contents.message[language].networkError, isChatbot: true }]);
                    setIsChatbotPending(false);
                    setIsStreaming(false);
                    clearStreamMessage();
                    ws.close();
                }
            };

            ws.onclose = () => {
                console.log('WebSocket connection closed');
            };

            ws.onerror = (error) => {
                setMessages([{ message: contents.message[language].networkError, isChatbot: true }]);
                setIsChatbotPending(false);
                setIsStreaming(false);
                clearStreamMessage();
                ws.close();
                console.warn('WebSocket error:', error);
            };
        },
        enabled: false,
    });
}

export const useSendPDPMessage = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { products, setProducts, clearProducts, setStructuredProducts } = useProductsStore();
    const { setMessages } = useMessagesStore();
    const { 
        currentItemId, 
        setIsChatbotPending, 
        setIsNewProduct, 
        setNewProductCount, 
        displayItemId, 
        setIsPDPChat, 
        setCurrentShrinkCardIndex, 
        setCurrentListCardIndex, 
        language,
        setIsLastIntentProductRecommendation
    } = useChatStateStore();
    const { setIsFormSubmitted } = useChatStateStore();
    const { refetch: refetchExample } = useExampleQuery();

    return useMutation({
        mutationFn: async (variables) => {
            try {
                setIsFormSubmitted('initial');
                const { chatType } = variables;
                const response = await chatApi.post(`${process.env.REACT_APP_CHAT_PRODUCT_ENDPOINT}/${partnerId}`, {
                    itemId: String(displayItemId),
                    chatUserId: String(chatUserId),
                    channelId: isMobileDevice ? 'mobile' : 'web',
                    type: chatType,
                });

                if (response.data.text) {
                    logToConsole(response.data);
                    if (response.data.product.length > 0) {
                        const newProducts = [...response.data.product, ...products];
                        const newStructuredProducts = groupProductsUsingReduce(response.data.product, 3);
                        clearProducts();
                        setProducts(newProducts);
                        setStructuredProducts(newStructuredProducts);
                        setIsNewProduct(true);
                        setNewProductCount(response.data.product.length);
                        setIsPDPChat(true);
                        setIsLastIntentProductRecommendation(true);
                        setCurrentShrinkCardIndex(0);
                        setCurrentListCardIndex(0);
                    } else {
                        setIsNewProduct(false);
                        setIsPDPChat(false);
                    }
                    setIsChatbotPending(false);
                    if (currentItemId.length > 0) {
                        refetchExample();
                    }
                    // "intent": "pdp_start_question",
                    setMessages([{ message: response.data.text[0], isChatbot: true, type: 'pdp' }]);
                    return response.data;
                }
            } catch (error) {
                setMessages([{ message: contents.message[language].networkError, isChatbot: true }])
                logToConsole({ textResp: error.message });
                setIsChatbotPending(false);
                console.log('error', error.message);
            }
        },
        enabled: false,
    });
}

export const useFetchProductInfo = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { currentItemId, setIsNewProduct, setNewProductCount, setIsChatbotPending, setCurrentShrinkCardIndex, setCurrentListCardIndex, currentPage } = useChatStateStore();
    const { products, setProducts, clearProducts, setStructuredProducts } = useProductsStore();
    const { refetch: refetchExample } = useExampleQuery();

    return useMutation({
        mutationFn: async (connectionId) => {
            let attempts = 0;
            const maxAttempts = 30;
            const retryDelay = 100;

            while (attempts < maxAttempts) {
                attempts++;
                const response = await chatApi.post(process.env.REACT_APP_CHATSTREAM_PRODUCT_ENDPOINT, {
                    externalKey: partnerId,
                    connectionId: String(connectionId),
                    chatUserId: String(chatUserId),
                });

                if (response.data.product && response.data.product.length > 0) {
                    const newProducts = [...response.data.product, ...products];
                    const newStructuredProducts = groupProductsUsingReduce(response.data.product, 3);
                    clearProducts();
                    setProducts(newProducts);
                    setStructuredProducts(newStructuredProducts);
                    setCurrentShrinkCardIndex(0);
                    setCurrentListCardIndex(0);
                    setIsNewProduct(true);
                    setNewProductCount(response.data.product.length);
                    break; // Exit the loop if products are found
                }

                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
            if (currentItemId.length > 0) {
                refetchExample();
            }
            setIsChatbotPending(false);

            // Optionally handle the case where no products were found after all attempts
            if (attempts === maxAttempts) {
                console.warn('Max attempts reached without finding products');
            }
        },
        enabled: false,
    });
}

export const useExampleQuery = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { currentItemId, setIsChatbotPending } = useChatStateStore();
    const { setExamples, setFollowupQuestion } = useProductsStore();
    const { messages } = useMessagesStore();

    return useQuery({
        queryKey: ['example', partnerId, currentItemId],
        queryFn: async () => {
            if (currentItemId.length === 0) {
                return { data: { examples: [], followupQuestion: null } };
            }
            await new Promise(resolve => {
                setTimeout(() => {
                    resolve();
                }, 3000);
            });

            let attempts = 0;
            const maxAttempts = 5;
            const retryDelay = 1000;

            while (attempts < maxAttempts) {
                attempts++;
                const response = await chatApi.get(`${process.env.REACT_APP_EXAMPLE_ENDPOINT}/${partnerId}`,
                    {
                        params: {
                            chatUserId: String(chatUserId),
                            partnerId: String(partnerId),
                            itemIds: currentItemId,
                        }
                    }
                );

                if (response.data.examples && response.data.examples.length > 0 && messages.length > 0) {
                    setExamples(response.data.examples);
                    setFollowupQuestion(response.data.followupQuestion);
                    logToConsole({ examples: response.data.examples, itemIds: currentItemId });
                    setIsChatbotPending(false);
                    return response;
                }

                if (response.data.examples.length === 0 && attempts < maxAttempts) {
                    logToConsole({ examples: response.data.examples, itemIds: currentItemId });
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }

            // Final attempt only if all retries failed
            const finalResponse = await chatApi.get(`${process.env.REACT_APP_EXAMPLE_ENDPOINT}/${partnerId}`,
                {
                    params: {
                        chatUserId: String(chatUserId),
                        partnerId: String(partnerId),
                        itemIds: currentItemId,
                    }
                }
            );
            // setExamples(finalResponse.data.examples || []);
            return finalResponse;
        },
        enabled: false,
    });
};

export const useResetQuery = () => {
    const { chatUserId, partnerId } = useUserStore();
    const { clearMessages } = useMessagesStore();
    const { clearProducts, clearStructuredProducts, clearExamples, clearFollowupQuestion } = useProductsStore();
    const { setIsPreAnswered, setIsFormSubmitted, setCurrentItemId, clearCurrentShrinkCardIndex, clearCurrentListCardIndex } = useChatStateStore();
    return useMutation({
        mutationFn: async () => {
            const response = await chatApi.post(`${process.env.REACT_APP_RENEW_ENDPOINT}/${partnerId}`, {
                chatUserId: String(chatUserId),
            });

            clearMessages();
            clearProducts();
            clearStructuredProducts();
            clearExamples();
            clearFollowupQuestion();
            clearCurrentShrinkCardIndex();
            clearCurrentListCardIndex();
            setCurrentItemId([]);
            setIsPreAnswered(false);
            setIsFormSubmitted('initial');
            return response;
        }
    });
}

export const useLoadHistory = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { setMessages, clearMessages } = useMessagesStore();
    const { setProducts, clearProducts, setStructuredProducts, clearStructuredProducts, clearExamples } = useProductsStore();

    return useQuery({
        queryKey: ['loadHistory', partnerId],
        queryFn: async () => {
            const response = await chatApi.get(`${process.env.REACT_APP_CHAT_ENDPOINT}/load/${partnerId}`, {
                params: {
                    partnerId: String(partnerId),
                    chatUserId: String(chatUserId),
                }
            });

            const formattedHistory = response.data.chatList
                .filter(message => message.text.length > 0)
                .map(message => ({
                    message: message.text,
                    isChatbot: message.role === 'assistant',
                }));
            const productList = response.data.chatList
                .filter(message => message.role === 'assistant')
                .map(message => [...message.product])
                .flat()
                .reverse();
            const structuredProductList = response.data.chatList
                .filter(message => message.role === 'assistant')
                .filter(message => message.product && message.product.length > 0)
                .reverse()
                .map(message => {
                    const reversedProducts = [...message.product].reverse();
                    const groupedProducts = groupProductsUsingReduce(reversedProducts, 3);
                    return groupedProducts;
                })
                .flat()
            clearMessages();
            clearProducts();
            clearStructuredProducts();
            if (productList.length === 0) {
                clearExamples();
            }
            setMessages(formattedHistory);
            setProducts(productList);
            setStructuredProducts(structuredProductList);
            return response;
        },
        enabled: false,
    });
}

export const useFormSubmitted = () => {
    const { chatUserId, partnerId } = useUserStore();
    const { currentItemId } = useChatStateStore();
    const { setMessages } = useMessagesStore();
    const { utmParams } = useUtmStore();

    return useMutation({
        mutationFn: async (variables) => {
            const { formData = {} } = variables;
            const response = await chatApi.post(`${process.env.REACT_APP_CSINQUIRY_ENDPOINT}/${partnerId}`,
                {
                    chatUserId: String(chatUserId),
                    channelId: 'web',
                    itemId: [String(currentItemId)],
                    formData: {
                        name: formData.name,
                        phone: formData.phone,
                        inquiry: formData.inquiry,
                    },
                },
                {
                    params: {
                        utm_source: utmParams?.source,
                        utm_medium: utmParams?.medium,
                        utm_campaign: utmParams?.campaign,
                        utm_content: utmParams?.content,
                        utm_term: utmParams?.term,
                        transitions_page: utmParams?.transitionPage,
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },

            );
            setMessages([{ message: response.data.text[0], isChatbot: true }])
            return response.text;
        },
    });
}

export const useFormSubmittedFastfive = () => {
    const { chatUserId, partnerId } = useUserStore();
    const { currentItemId } = useChatStateStore();
    const { setMessages } = useMessagesStore();
    const { utmParams } = useUtmStore();

    return useMutation({
        mutationFn: async (variables) => {
            const { formData = {} } = variables;
            const response = await chatApi.post(`${process.env.REACT_APP_FORM_ENDPOINT}/${partnerId}`,
                {
                    chatUserId: String(chatUserId),
                    channelId: 'web',
                    itemId: [String(currentItemId)],
                    formData: {
                        name: formData.name,
                        phone: formData.phone,
                        email: formData.email,
                        checkPrivacyPolicy: formData.checkPrivacyPolicy,
                        checkMarketing: formData.checkMarketing,
                    },
                },
                {
                    params: {
                        utm_source: utmParams?.source,
                        utm_medium: utmParams?.medium,
                        utm_campaign: utmParams?.campaign,
                        utm_content: utmParams?.content,
                        utm_term: utmParams?.term,
                        transitions_page: utmParams?.transitionPage,
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },

            );
            setMessages([{ message: response.data.text[0], isChatbot: true }])
            return response.text;
        },
    });
}

export const useUserEvent = () => {
    const { chatUserId, partnerId, isMobileDevice } = useUserStore();

    return useMutation({
        mutationFn: async (variables) => {
            const response = await chatApi.post(`${process.env.REACT_APP_USEREVENT_ENDPOINT}/${partnerId}`, {
                channelId: String(isMobileDevice ? 'mobile' : 'web'),
                eventCategory: String(variables.eventCategory),
                chatUserId: String(chatUserId),
                products: [{ ...variables.product, quantity: 1 }],
            });
            return response;
        },
    });
}

export const useHistoryUpdate = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { displayLocation } = useChatStateStore();

    return useMutation({
        mutationFn: async (variables) => {
            const payload = {
                chatUserId: String(chatUserId),
                pageUrl: variables.url,
                displayLocation: displayLocation,
                itemId: variables.itemId,
            }
            const response = await chatApi.post(`${process.env.REACT_APP_HISTORY_ENDPOINT}/${partnerId}`, payload);
            return response.data.visitedState;
        },
    });
}

export const useWSSOpen = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { setMessages, streamMessage, clearStreamMessage, setStreamMessage, guidelineContent, guidelineTitle, guidelineId, isBuilder, setIsBuilder } = useMessagesStore();
    const { setIsStreaming, connectionId, setConnectionId, currentItemId, setIsChatbotPending, setIsPDPChat, setIsNewProduct, language, isConsole } = useChatStateStore();
    const { setExamples, clearFollowupQuestion } = useProductsStore();

    return useMutation({
        mutationFn: async (variables) => {
            const { messageInput, preQuestionAnswers = [], preAnswers = [] } = variables;

            let messageOutput = '';
            let localConnectionId = null;
            const ws = new WebSocket(process.env.REACT_APP_WSS_CHAT);

            setIsNewProduct(false);
            setIsPDPChat(false);

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    "action": "chat",
                    "chatUserId": chatUserId,
                    "channelId": isMobileDevice ? 'mobile' : 'web',
                    "requestMessage": 'connection test',
                    "itemIds": currentItemId,
                    "preQuestionAnswers": preQuestionAnswers || [],
                    "preAnswers": preAnswers || [],
                    "externalKey": partnerId,
                    "connectionId": connectionId,
                    "guidelineId": guidelineId,
                    "guidelineTitle": guidelineTitle,
                    "guidelineContent": guidelineContent,
                    "isConsole": isConsole,
                }));
            };
        },
        enabled: false,
    });
}